/* External */
import {
  memo,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import Icon from '@dtvgo/icon';
import { ClassNames } from '~/utils/misc';

/* Components */
import ScheduleCard from '../ScheduleCard';
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';

/* Providers */
import { useUserAccount } from '../../providers/UserProvider';

/* Hooks */
import useLockIcon from '../../hooks/useLockIcon';
import useContent from '../../hooks/useContent';
import useFetchClickAction from '../../hooks/useFetchClickAction';

/* Utils */
import useCurrentAndNextSchedulesAndFetch from '../../hooks/useCurrentAndNextSchedulesAndFetch';
import { redirectToPlayer, redirectToDetail } from '../../utils/history';
import { Channel } from '../../storage/models';
import { noOp } from '../../utils/functions';
import { getImage } from '../../utils/imageUtils';
import { ScrollPosition } from '../ScrollView';
import { PLAY_SOURCE_SECTION, getPlaySourcePage } from '../../utils/trackSourceConviva';
import { formatPlaySource, getParentLander, GTM_EVENT_NAMES } from '../../utils/googleTags';

/* Styles */
import './ChannelLane.scss';

const ChannelLaneClassNames = ClassNames.component('channel-lane');

const CHANNEL_IMAGE_WIDTH = 80;

/**
 * Builds clickLearnAction parameters
 * @param {Object} content - Content object
 * @param {Object} userData
 * @param {string} playSource
 * @returns {Object} Formatted parameters for clickLearnAction
 */
const buildClickActionParams = (content, userData = {}, playSource = '') => {
  const { msoProvider, accountType: userType } = userData;

  return {
    actionOrigin: content?.actionOrigin || '',
    userOrigin: {
      playSource: playSource || '',
      userLogin: userType || '',
      msoProvider: msoProvider || '',
    },
    contentType: content?.isLive ? 1 : 2,
    guideId: content?.streaming?.id || content?.vrioAssetId || '',
    vrioAssetId: content?.vrioAssetId || '',
  };
};

function ChannelLane({
  className = '',
  channel = undefined,
  position = 0,
  onArrowPress = noOp,
  onBecameFocused = noOp,
  pageId = '',
  isLoading = false,
  shouldStealFocus = false,
  withoutFilter = false,
  biggerLogo = false,
  epgCommonTagEvent = noOp,
}) {
  const { images } = channel || {};

  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const playSourcePage = useMemo(() => getPlaySourcePage(pageId), [pageId]);

  const { currentAndNextSchedules, isFetching } = useCurrentAndNextSchedulesAndFetch(channel, 2);
  const imageUrl = useMemo(() => getImage(images, CHANNEL_IMAGE_WIDTH), [images]);

  const currentSchedule = currentAndNextSchedules?.get(0);
  const nextSchedule = currentAndNextSchedules?.get(1);

  const { fetchClickAction } = useFetchClickAction();

  const currentLiveContent = useContent(currentSchedule?.content);
  const nextLiveContent = useContent(nextSchedule?.content);

  const setInitialSectionIndexEpg = useCallback(() => {
    sessionStorage.setItem('initialSectionIndexEpg', position + 1);
    sessionStorage.setItem('initialSectionIndex', -1);
  }, [position]);

  const handleFocusedOrLiveContentChanged = useCallback(({ schedule, isCurrent }) => {
    setInitialSectionIndexEpg();
    onBecameFocused(position, { schedule, isCurrent });
  }, [onBecameFocused, position, setInitialSectionIndexEpg]);

  const locationState = useCallback(() => ({
    actionOrigin: currentLiveContent?.actionOrigin || '',
    transmissionType: channel?.schema,
    sectionName: 'epg',
    componentType: 'epg',
    gtmPlaySource: formatPlaySource({
      parentLander: getParentLander(pageId),
      section: 'epg',
    }),
  }), [channel?.schema, pageId, currentLiveContent]);

  const handleEpgTagEvent = useCallback(({ content, schedule, isCurrent }) => {
    epgCommonTagEvent({
      eventName: isCurrent ? GTM_EVENT_NAMES.EPG.LIVE_NOW : GTM_EVENT_NAMES.EPG.NEXT_LIVE,
      extraData: {
        content,
        startTime: schedule?.startTime,
        endTime: schedule?.endTime,
      },
    });
  }, [epgCommonTagEvent]);

  const handleRedirectToPlayer = useCallback(({ schedule }) => {
    setInitialSectionIndexEpg();
    handleEpgTagEvent({
      content: currentLiveContent,
      schedule,
      isCurrent: true,
    });

    const clickActionParams = buildClickActionParams(
      currentLiveContent,
      { msoProvider, accountType: userType },
      playSourcePage,
    );
    fetchClickAction(clickActionParams);

    if (!schedule) {
      redirectToPlayer(channel, {
        page: playSourcePage,
        section: PLAY_SOURCE_SECTION.EPG,
        state: locationState(),
      });
    } else if (!schedule?.blackout) {
      redirectToPlayer(schedule, {
        page: playSourcePage,
        section: PLAY_SOURCE_SECTION.EPG,
        state: locationState(),
      });
    }
  }, [
    setInitialSectionIndexEpg,
    handleEpgTagEvent,
    currentLiveContent,
    channel,
    playSourcePage,
    locationState,
    fetchClickAction,
    msoProvider,
    userType,
  ]);

  const handleRedirectDetail = useCallback(({ schedule }) => {
    handleEpgTagEvent({
      content: nextLiveContent,
      schedule,
      isCurrent: false,
    });
    setInitialSectionIndexEpg();

    const clickActionParams = buildClickActionParams(
      nextLiveContent,
      { msoProvider, accountType: userType },
      playSourcePage,
    );
    fetchClickAction(clickActionParams);

    if (!schedule) {
      redirectToDetail(channel, { state: locationState() });
    } else if (!schedule?.blackout) {
      redirectToDetail(schedule, { state: locationState() });
    }
  }, [
    channel,
    handleEpgTagEvent,
    locationState,
    nextLiveContent,
    setInitialSectionIndexEpg,
    fetchClickAction,
    msoProvider,
    playSourcePage,
    userType,
  ]);

  const showLockIcon = useLockIcon(channel);

  const imageChannel = useMemo(() => (
    <div className={ChannelLaneClassNames.element('logo-box')}>
      <img
        className={ChannelLaneClassNames.element('logo', { lock: showLockIcon })}
        src={imageUrl}
        alt={channel?.title || ''}
      />
      {showLockIcon && <Icon name="lock" className={ChannelLaneClassNames.element('icon-lock')} />}
    </div>
  ), [showLockIcon, imageUrl, channel]);

  return (
    <div className={ChannelLaneClassNames.block({ 'without-filter': withoutFilter, 'bigger-logo': biggerLogo }, className)}>
      {imageChannel}
      <ScrollIntoViewIfFocused
        className={ChannelLaneClassNames.element('schedule-card-container')}
        position={ScrollPosition.CENTER}
      >
        <ScheduleCard
          schedule={currentSchedule}
          onFocusedOrLiveContentChanged={handleFocusedOrLiveContentChanged}
          isCurrent
          isFetching={isLoading || isFetching}
          onEnterPress={handleRedirectToPlayer}
          onArrowPress={onArrowPress}
          shouldStealFocus={shouldStealFocus}
        />
      </ScrollIntoViewIfFocused>
      <ScrollIntoViewIfFocused
        className={ChannelLaneClassNames.element('schedule-card-container')}
        position={ScrollPosition.CENTER}
      >
        <ScheduleCard
          schedule={nextSchedule}
          onFocusedOrLiveContentChanged={handleFocusedOrLiveContentChanged}
          isFetching={isLoading || isFetching}
          onArrowPress={onArrowPress}
          onEnterPress={handleRedirectDetail}
        />
      </ScrollIntoViewIfFocused>
    </div>
  );
}

ChannelLane.propTypes = {
  className: PropTypes.string,
  channel: PropTypes.instanceOf(Channel),
  position: PropTypes.number,
  onArrowPress: PropTypes.func,
  onBecameFocused: PropTypes.func,
  pageId: PropTypes.string,
  isLoading: PropTypes.bool,
  shouldStealFocus: PropTypes.bool,
  withoutFilter: PropTypes.bool,
  biggerLogo: PropTypes.bool,
  epgCommonTagEvent: PropTypes.func,
};

export default memo(ChannelLane);
